// src/components/map/hooks/adapters/useFacilityClickHandler.ts
//
// Sub-hook for useFacilityHighlightAdapter.
// Manages facility/well click interactions, context menu state,
// and the clicked entity tracking on the Mapbox map.
//
// The consuming adapter is responsible for composing this with other
// sub-hooks and wiring cross-cutting concerns (clearEverything, hotkeys).
import { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";

import { Feature, Point, featureCollection } from "@turf/helpers";
import type { AnyLayer, EventData, PointLike } from "mapbox-gl";
import { useMapStore } from "stores/map";
import {
  selectDisplaySettings,
  selectHighlightContributingWellsForFacility
} from "stores/map/mapSelectors";
import { MouseInteraction } from "types/maps/interactions";

import {
  FACILITY_LAYER,
  FACILITY_TYPE_ENUM,
  SPIDER_FACILITY_LAYER,
  WELL_LAYER
} from "../../../../constants";
import { EntityKind } from "../../../../models/entityKind";
import type { RootState } from "../../../../store/rootReducer";
import { clearGeojsonSource, setGeojsonSource } from "../../utils/geojsonSource";
import { useMapContext } from "../useMapContext";

// ── Constants ────────────────────────────────────────────────────────────────

const CLICKED_FACILITY = "clicked-facility";
const CLICKED_WELL = "clicked-well";

// ── Types ────────────────────────────────────────────────────────────────────

export interface ClickedEntity {
  readonly facility: Maybe<{
    readonly facilityId: string;
    readonly geometry: Maybe<GeoJSON.FeatureCollection>;
  }>;
  readonly well: Maybe<{
    readonly wellId: string;
    readonly geometry: GeoJSON.FeatureCollection;
  }>;
}

export interface UseFacilityClickHandlerParams {
  /** Restore original icon-color expression on the facility layer (from useSpiderifyLayer). */
  readonly restoreOriginalColors: () => void;
  /** Remove all spider layers from the map (from useSpiderifyLayer). */
  readonly removeAllSpiderLayers: () => void;
}

export interface UseFacilityClickHandlerReturn {
  /** The currently clicked entity (facility and/or well). */
  readonly clickedEntity: Maybe<ClickedEntity>;
  /** Update the clicked entity (used by contribution highlight for Redux triggers). */
  readonly setClickedEntity: (entity: Maybe<ClickedEntity>) => void;
  /** Whether the context menu is visible. */
  readonly showContextMenu: boolean;
  /** Setter for context menu visibility (used by UI for dismiss). */
  readonly setShowContextMenu: (show: boolean) => void;
  /** Context menu position in screen coordinates. */
  readonly contextMenuPosition: { readonly x: number; readonly y: number };
  /** Hide the context menu and clean up spider layers. */
  readonly hideContextMenu: () => void;
  /** Clear a GeoJSON source on the map. */
  readonly clearSource: (layer: string) => void;
  /** Set data on an existing GeoJSON source. Returns true on success. */
  readonly setSource: (layer: string, geojson: GeoJSON.FeatureCollection) => boolean;
}

// ── Hook ─────────────────────────────────────────────────────────────────────

/**
 * Sub-hook that manages facility/well click interactions and context menu state.
 *
 * Responsibilities:
 * - Tracks the currently clicked entity (facility or well)
 * - Registers click and context-menu event handlers on facility/spider/well layers
 * - Manages context menu visibility and position
 * - Delegates spiderify cleanup to the spiderify sub-hook on context menu hide
 * - Exposes map source helpers for other sub-hooks
 */
export function useFacilityClickHandler(
  params: UseFacilityClickHandlerParams
): UseFacilityClickHandlerReturn {
  const { restoreOriginalColors, removeAllSpiderLayers } = params;

  // ── Map context ────────────────────────────────────────────────────

  const { mapbox } = useMapContext();

  // ── Store reads ────────────────────────────────────────────────────

  const displaySettings = useMapStore(selectDisplaySettings);
  const activeEntityKinds = useSelector(
    (state: RootState) => state.app.activeEntityKinds
  );
  const highlightContributingWellsForFacility = useMapStore(
    selectHighlightContributingWellsForFacility
  );
  const setSelectedFacilitiesAction = useMapStore((s) => s.setSelectedFacilities);
  const setHighlightContributingWellsForFacility = useMapStore(
    (s) => s.setHighlightContributingWellsForFacility
  );

  // ── Refs ───────────────────────────────────────────────────────────

  /** Tracks the current Redux highlight-contributing-wells value for use in closures. */
  const highlightContributingWellsForFacilityRef = useRef<Maybe<string>>(null);

  // ── Local state ────────────────────────────────────────────────────

  const [clickedEntity, setClickedEntity] = useState<Maybe<ClickedEntity>>({
    facility: null,
    well: null
  });
  const [showContextMenu, setShowContextMenu] = useState(false);
  const [contextMenuPosition, setContextMenuPosition] = useState({ x: 0, y: 0 });

  // ── Sync ref with store value ──────────────────────────────────────

  useEffect(() => {
    highlightContributingWellsForFacilityRef.current =
      highlightContributingWellsForFacility;
  }, [highlightContributingWellsForFacility]);

  // ── Helpers ────────────────────────────────────────────────────────

  const clearSource = useCallback(
    (layer: string) => clearGeojsonSource(mapbox, layer),
    [mapbox]
  );

  const setSource = useCallback(
    (layer: string, geojson: GeoJSON.FeatureCollection) =>
      setGeojsonSource(mapbox, layer, geojson),
    [mapbox]
  );

  const getBoundingBox = useCallback((e: EventData): [PointLike, PointLike] => {
    const buffer = 1;
    return [
      [e.point.x - buffer, e.point.y - buffer],
      [e.point.x + buffer, e.point.y + buffer]
    ] as [PointLike, PointLike];
  }, []);

  const showContextMenuAt = useCallback((e: EventData) => {
    setShowContextMenu(false);
    setTimeout(() => {
      setContextMenuPosition({ x: e.point.x, y: e.point.y });
      setShowContextMenu(true);
    }, 100);
  }, []);

  // ── Click handlers ─────────────────────────────────────────────────

  const onClickFacility = useCallback(
    (e: EventData) => {
      if (!mapbox) return;
      e.preventDefault?.();
      const bbox = getBoundingBox(e);
      const layerIds = [FACILITY_LAYER, SPIDER_FACILITY_LAYER].filter((id) =>
        mapbox.getLayer(id)
      );

      const features = mapbox.queryRenderedFeatures(bbox, {
        layers: layerIds
      }) as Feature<Point>[];

      const addClickedLayer = (geojson: GeoJSON.FeatureCollection) => {
        if (setGeojsonSource(mapbox, CLICKED_FACILITY, geojson)) return;

        const layer = {
          id: CLICKED_FACILITY,
          type: "symbol",
          source: { type: "geojson", data: geojson },
          paint: { "icon-color": "black" },
          layout: {
            "icon-image": [
              "match",
              ["get", "facilityType"],
              "CompressorStation",
              "compressor-station",
              FACILITY_TYPE_ENUM.GasPlant,
              "gas-plant",
              "Battery",
              "battery",
              "MeteringStation",
              "metering-station",
              "WastePlant",
              "waste-plant",
              "InjectionDisposalFacility",
              "injection-facility",
              "GatheringSystem",
              "gathering-system",
              /* default */ "default-facility"
            ],
            "icon-size": [
              "interpolate",
              ["linear"],
              ["zoom"],
              6,
              0.3,
              10,
              1.1,
              12,
              1.6,
              22,
              2.1
            ]
          }
        } as AnyLayer;
        mapbox.addLayer(layer);
      };

      if (features.length > 0) {
        const clickedFeature = featureCollection([features[0]]);
        setClickedEntity({
          facility: {
            facilityId: features[0].properties?.Uwi,
            geometry: clickedFeature
          },
          well: null
        });

        clearGeojsonSource(mapbox, CLICKED_FACILITY);
        clearGeojsonSource(mapbox, CLICKED_WELL);

        if (e.type === "contextmenu") {
          showContextMenuAt(e);
        }

        const lid = (features[0] as mapboxgl.MapboxGeoJSONFeature).layer.id;
        if (lid === FACILITY_LAYER) {
          addClickedLayer(clickedFeature);
        }
      }
    },
    [mapbox, getBoundingBox, showContextMenuAt]
  );

  const onClickWell = useCallback(
    (e: EventData) => {
      if (!mapbox || !activeEntityKinds.includes(EntityKind.Facility)) return;

      const buffer = 2;
      const bbox = [
        [e.point.x - buffer, e.point.y - buffer],
        [e.point.x + buffer, e.point.y + buffer]
      ] as [PointLike, PointLike];

      const features = mapbox.queryRenderedFeatures(bbox, {
        layers: [WELL_LAYER]
      });

      const addClickedLayer = (geojson: GeoJSON.FeatureCollection) => {
        if (setGeojsonSource(mapbox, CLICKED_WELL, geojson)) return;

        const layer = {
          id: CLICKED_WELL,
          type: "line",
          source: { type: "geojson", data: geojson },
          paint: { "line-color": "black", "line-width": 5 }
        } as AnyLayer;
        mapbox.addLayer(layer);
      };

      if (features.length > 0) {
        const clickedFeature = featureCollection([features[0]]);
        setClickedEntity({
          well: {
            wellId: features[0].properties?.Uwi,
            geometry: clickedFeature
          },
          facility: null
        });
        clearGeojsonSource(mapbox, CLICKED_FACILITY);
        addClickedLayer(clickedFeature);
        if (e.type === "contextmenu") {
          showContextMenuAt(e);
        }
      }
    },
    [mapbox, activeEntityKinds, showContextMenuAt]
  );

  // ── Public actions ─────────────────────────────────────────────────

  const hideContextMenu = useCallback(() => {
    setShowContextMenu(false);
    removeAllSpiderLayers();
    restoreOriginalColors();
  }, [removeAllSpiderLayers, restoreOriginalColors]);

  // ── Effect: facility click & context-menu event registration ───────

  useEffect(() => {
    if (!mapbox) return;
    if (!activeEntityKinds.includes(EntityKind.Facility)) return;

    const setSelectedFacility = (e: EventData) => {
      const bbox = getBoundingBox(e);
      const layerIds = [FACILITY_LAYER, SPIDER_FACILITY_LAYER].filter((id) =>
        mapbox.getLayer(id)
      );

      const features = mapbox.queryRenderedFeatures(bbox, {
        layers: layerIds
      }) as Feature<Point>[];

      if (!features.length) return;

      const facilityId = features[0].properties?.Uwi;
      const selectedFacilities: Record<
        string,
        { Uwi: string; color: string; group: string }
      > = {};
      selectedFacilities[facilityId] = { Uwi: facilityId, color: "", group: "" };

      setSelectedFacilitiesAction(selectedFacilities);

      if (highlightContributingWellsForFacilityRef.current !== null) {
        setHighlightContributingWellsForFacility(facilityId);
      }
    };

    const hoverLayers = [FACILITY_LAYER, SPIDER_FACILITY_LAYER];

    mapbox.on(MouseInteraction.Click, hoverLayers, setSelectedFacility);
    mapbox.on(MouseInteraction.ContextMenu, hoverLayers, onClickFacility);
    mapbox.on(MouseInteraction.Click, hideContextMenu);

    return () => {
      hoverLayers.forEach((layer) => {
        mapbox.off(MouseInteraction.Click, layer, setSelectedFacility);
        mapbox.off(MouseInteraction.ContextMenu, layer, onClickFacility);
      });
      mapbox.off(MouseInteraction.Click, hideContextMenu);
    };
  }, [
    mapbox,
    activeEntityKinds,
    displaySettings?.facilityIconSize,
    onClickFacility,
    hideContextMenu,
    getBoundingBox,
    setSelectedFacilitiesAction,
    setHighlightContributingWellsForFacility
  ]);

  // ── Effect: well click event registration ──────────────────────────

  useEffect(() => {
    if (!mapbox) return;

    mapbox.on(MouseInteraction.Click, WELL_LAYER, onClickWell);
    mapbox.on(MouseInteraction.ContextMenu, WELL_LAYER, onClickWell);

    return () => {
      mapbox.off(MouseInteraction.Click, WELL_LAYER, onClickWell);
      mapbox.off(MouseInteraction.ContextMenu, WELL_LAYER, onClickWell);
    };
  }, [mapbox, onClickWell]);

  // ── Return ─────────────────────────────────────────────────────────

  return {
    clickedEntity,
    setClickedEntity,
    showContextMenu,
    setShowContextMenu,
    contextMenuPosition,
    hideContextMenu,
    clearSource,
    setSource
  };
}
